import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";

interface AddTaskSheetProps {
  isOpen: boolean;
  onClose: () => void;
  businessId?: Id<"businesses">;
  onTaskAdded?: () => void;
}

export function AddTaskSheet({ isOpen, onClose, businessId, onTaskAdded }: AddTaskSheetProps) {
  // State for form fields
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [priority, setPriority] = useState("low"); // Default to low priority
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch organization members
  const members = useQuery(api.leadTasks.getOrgMembers) || [];

  // Create task mutation
  const createTask = useMutation(api.leadTasks.createTask);

  // Handle form submission
  const handleSubmit = async () => {
    if (!title || !dueDate || !assignedTo || !businessId) return;

    setIsSubmitting(true);
    try {
      await createTask({
        businessId,
        title,
        dueDate,
        status: "pending",
        assignedTo,
        priority, // Add priority to the task
      });

      // Reset form and close sheet
      setTitle("");
      setDueDate("");
      setAssignedTo("");
      setPriority("low"); // Reset priority to default
      if (onTaskAdded) onTaskAdded();
      onClose();
    } catch (error) {
      console.error("Error creating task:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:max-w-[400px]">
        <SheetHeader>
          <SheetTitle>Add Task</SheetTitle>
          <SheetDescription>Create a new task for this lead.</SheetDescription>
        </SheetHeader>
        <div className="space-y-6 py-6">
          <div className="space-y-2">
            <Label htmlFor="title">Task Title</Label>
            <Input
              id="title"
              placeholder="Enter task title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="due-date">Due Date</Label>
            <Input
              id="due-date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="priority">Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger>
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="assigned">Assigned To</Label>
            <Select value={assignedTo} onValueChange={setAssignedTo}>
              <SelectTrigger>
                <SelectValue placeholder="Select team member" />
              </SelectTrigger>
              <SelectContent>
                {members.length === 0 ? (
                  <SelectItem value="loading" disabled>
                    No team members found
                  </SelectItem>
                ) : (
                  members.map((member: any) => (
                    <SelectItem key={member._id} value={member.name}>
                      {member.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={isSubmitting || !title || !dueDate || !assignedTo}
          >
            {isSubmitting ? "Creating..." : "Create Task"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
